import { useEffect } from "react";

/**
 * useDocumentMeta — set document title + OG/Twitter meta tags for a page.
 *
 * Lightweight alternative to react-helmet for our single use case
 * (PublicDealDetailPage). Cleans up appended tags on unmount so we don't
 * pollute the head when navigating to another route.
 */
export function useDocumentMeta({ title, description, image, url, type = "website" }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const tags = [
      { selector: 'meta[name="description"]', attr: "name",     name: "description",   content: description },
      { selector: 'meta[property="og:title"]',       attr: "property", name: "og:title",       content: title },
      { selector: 'meta[property="og:description"]', attr: "property", name: "og:description", content: description },
      { selector: 'meta[property="og:image"]',       attr: "property", name: "og:image",       content: image },
      { selector: 'meta[property="og:url"]',         attr: "property", name: "og:url",         content: url },
      { selector: 'meta[property="og:type"]',        attr: "property", name: "og:type",        content: type },
      { selector: 'meta[name="twitter:card"]',        attr: "name", name: "twitter:card",        content: image ? "summary_large_image" : "summary" },
      { selector: 'meta[name="twitter:title"]',       attr: "name", name: "twitter:title",       content: title },
      { selector: 'meta[name="twitter:description"]', attr: "name", name: "twitter:description", content: description },
      { selector: 'meta[name="twitter:image"]',       attr: "name", name: "twitter:image",       content: image },
    ].filter(t => t.content);

    const previous = tags.map(t => {
      const existing = document.head.querySelector(t.selector);
      const had = existing
        ? { el: existing, prev: existing.getAttribute("content"), created: false }
        : { el: null, prev: null, created: true };
      const el = existing || document.createElement("meta");
      el.setAttribute(t.attr, t.name);
      el.setAttribute("content", t.content);
      if (!existing) document.head.appendChild(el);
      return { ...had, el, attr: t.attr, name: t.name };
    });

    return () => {
      document.title = prevTitle;
      previous.forEach(p => {
        if (p.created) p.el.remove();
        else if (p.prev != null) p.el.setAttribute("content", p.prev);
      });
    };
  }, [title, description, image, url, type]);
}
